import { LitElement, html, css } from 'lit';

/**
 * CrusherPaymentMethodCard
 *
 * Billing-page primitive — displays a customer's saved payment method (card
 * brand + last 4 + expiry) with a CTA slot for manage/update actions.
 *
 * Designed for products using a billing provider that hosts the actual card
 * collection / management UI (Polar, Stripe Billing customer portal, Paddle).
 * The product fetches saved-card metadata via the billing API and passes it
 * here; the consumer wires the CTA button (typically a link to the provider's
 * hosted portal) in the `cta` slot.
 *
 * Two states:
 *   - has-card    — render brand + last4 + expiry + optional billing email
 *   - none        — render "No payment method on file" + the cta slot
 *
 * Properties:
 *   - brand        — card brand string ("visa" / "mastercard" / "amex" /
 *                    "discover" / "diners" / "jcb" / "unionpay" / "unknown")
 *                    Case-insensitive; matches Stripe / Polar / Paddle convention.
 *   - last4        — last 4 digits string (e.g., "4242")
 *   - exp-month    — expiry month (1-12)
 *   - exp-year     — expiry year (4-digit)
 *   - email        — optional billing email displayed under the card
 *   - default      — boolean; show a "Default" badge
 *   - expired      — boolean; render with warning tone + "Expired" badge
 *
 * Slots:
 *   - cta — primary action button (typical: "Manage payment methods" linking
 *           to the provider's hosted customer portal)
 *
 * A11y: role=region with aria-label combining brand + last4 so screen readers
 *       announce "Visa ending in 4242".
 *
 * Why no event handlers: card-management actions are inherently external
 * (provider-hosted portal). This is a display surface, not interactive.
 */
export class CrusherPaymentMethodCard extends LitElement {
  static properties = {
    brand:    { type: String, reflect: true },
    last4:    { type: String, reflect: true },
    expMonth: { type: Number, attribute: 'exp-month', reflect: true },
    expYear:  { type: Number, attribute: 'exp-year', reflect: true },
    email:    { type: String, reflect: true },
    default:  { type: Boolean, reflect: true },
    expired:  { type: Boolean, reflect: true }
  };

  constructor() {
    super();
    this.brand = '';
    this.last4 = '';
    this.expMonth = 0;
    this.expYear = 0;
    this.email = '';
    this.default = false;
    this.expired = false;
  }

  static styles = css`
    :host {
      display: block;
      background: var(--crusher-surface);
      color: var(--crusher-fg);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-lg);
      padding: var(--crusher-spacing-5);
    }
    :host([expired]) {
      border-color: color-mix(in srgb, var(--crusher-feedback-danger) 40%, var(--crusher-border));
      background: color-mix(in srgb, var(--crusher-feedback-danger) 3%, var(--crusher-surface));
    }

    .row {
      display: flex;
      align-items: center;
      gap: var(--crusher-spacing-4);
      flex-wrap: wrap;
    }

    .brand-icon {
      flex: none;
      width: 2.5rem;
      height: 1.75rem;
      border-radius: var(--crusher-radius-sm);
      background: color-mix(in srgb, var(--crusher-fg) 8%, transparent);
      color: var(--crusher-fg);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: var(--crusher-font-weight-bold);
      font-size: var(--crusher-font-size-xs);
      letter-spacing: 0.05em;
    }
    /* Brand-tinted backgrounds — recognized brands get a hint of color.
       Stays restrained per the professional dialect's trust-signaling stance. */
    .brand-icon[data-brand="visa"]       { background: #1a1f71; color: #fff; }
    .brand-icon[data-brand="mastercard"] { background: #eb001b; color: #fff; }
    .brand-icon[data-brand="amex"]       { background: #006fcf; color: #fff; }
    .brand-icon[data-brand="discover"]   { background: #ff6000; color: #fff; }

    .details {
      flex: 1;
      min-width: 12rem;
      display: flex;
      flex-direction: column;
      gap: var(--crusher-spacing-1);
    }

    .label {
      font-weight: var(--crusher-font-weight-semibold);
      font-size: var(--crusher-font-size-sm);
      line-height: 1.4;
      display: flex;
      align-items: center;
      gap: var(--crusher-spacing-2);
    }

    .meta {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-xs);
      line-height: 1.4;
    }

    .badges {
      display: inline-flex;
      gap: var(--crusher-spacing-2);
    }
    .badge {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      border-radius: var(--crusher-radius-full);
      font-size: var(--crusher-font-size-xs);
      font-weight: var(--crusher-font-weight-medium);
      line-height: 1.4;
    }
    .badge-default {
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 14%, transparent);
      color: var(--crusher-color-brand-primary);
    }
    .badge-expired {
      background: color-mix(in srgb, var(--crusher-feedback-danger) 14%, transparent);
      color: var(--crusher-feedback-danger);
    }

    .cta {
      flex: none;
    }
    .cta ::slotted(*) {
      font: inherit;
    }

    .empty {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-sm);
      flex: 1;
    }
  `;

  _brandLabel(brand) {
    const b = (brand || '').toLowerCase();
    switch (b) {
      case 'visa':       return 'VISA';
      case 'mastercard': return 'MC';
      case 'amex':
      case 'american_express':
      case 'american express':
        return 'AMEX';
      case 'discover':   return 'DISC';
      case 'diners':
      case 'diners_club':
        return 'DINERS';
      case 'jcb':        return 'JCB';
      case 'unionpay':   return 'UP';
      default:           return 'CARD';
    }
  }

  _brandDisplay(brand) {
    const b = (brand || '').toLowerCase();
    switch (b) {
      case 'visa':       return 'Visa';
      case 'mastercard': return 'Mastercard';
      case 'amex':
      case 'american_express':
      case 'american express':
        return 'American Express';
      case 'discover':   return 'Discover';
      case 'diners':
      case 'diners_club':
        return 'Diners Club';
      case 'jcb':        return 'JCB';
      case 'unionpay':   return 'UnionPay';
      default:           return 'Card';
    }
  }

  _expiryDisplay() {
    if (!this.expMonth || !this.expYear) return '';
    const mm = String(this.expMonth).padStart(2, '0');
    /* Display 2-digit year (1234 → 34) to match standard card-display convention */
    const yy = String(this.expYear).slice(-2);
    return `Expires ${mm}/${yy}`;
  }

  render() {
    const hasCard = Boolean(this.last4);
    const brandKey = (this.brand || '').toLowerCase();
    const ariaLabel = hasCard
      ? `${this._brandDisplay(this.brand)} ending in ${this.last4}`
      : 'No payment method on file';

    if (!hasCard) {
      return html`
        <div class="row" role="region" aria-label=${ariaLabel}>
          <div class="empty">No payment method on file.</div>
          <div class="cta"><slot name="cta"></slot></div>
        </div>
      `;
    }

    const expiry = this._expiryDisplay();

    return html`
      <div class="row" role="region" aria-label=${ariaLabel}>
        <div class="brand-icon" data-brand=${brandKey} aria-hidden="true">${this._brandLabel(this.brand)}</div>
        <div class="details">
          <span class="label">
            ${this._brandDisplay(this.brand)} ending in ${this.last4}
            ${this.default || this.expired ? html`
              <span class="badges">
                ${this.default ? html`<span class="badge badge-default">Default</span>` : null}
                ${this.expired ? html`<span class="badge badge-expired">Expired</span>` : null}
              </span>
            ` : null}
          </span>
          ${expiry ? html`<span class="meta">${expiry}</span>` : null}
          ${this.email ? html`<span class="meta">${this.email}</span>` : null}
        </div>
        <div class="cta"><slot name="cta"></slot></div>
      </div>
    `;
  }
}

customElements.define('crusher-payment-method-card', CrusherPaymentMethodCard);
